import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components/macro'
import { useNavigate, useLocation } from 'react-router-dom'
import { PiList, PiMagnifyingGlass } from 'react-icons/pi'
import { NotificationButton } from 'components/Notification/NotificationButton'
import { useAuth } from 'context/authentication/useAuth'
import { useLeftNavStore } from 'components/Layout/LeftNav'
import { Z_INDEX } from 'styles/z-index'
import { Breadcrumbs } from './Breadcrumbs'
import type { BreadcrumbItem } from './types'
import logoWhite from 'assets/Name - White Transparent.svg'

const HeaderWrapper = styled.header`
  display: flex;
  align-items: center;
  height: var(--header-height);
  min-height: var(--header-height);
  background: var(--nav-bg-primary);
  padding-right: 24px;
  z-index: ${Z_INDEX.NAV};
`

const MenuToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--left-nav-width);
  height: 100%;
  border: none;
  background: transparent;
  color: var(--nav-text-muted);
  cursor: pointer;
  transition: color 0.15s ease-in-out;

  &:hover {
    color: var(--nav-text-primary);
  }
`

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 130px;
  padding: 18px 0;
`

const Logo = styled.img`
  height: 45px;
`

const BreadcrumbsContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  padding: 0 24px;
  min-width: 0;
`

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const SearchBar = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 220px;
  height: 32px;
  padding: 4px 8px;
  background: #e5ece7;
  border: none;
  border-radius: 8px;
  color: #6e6e6e;
  font-size: 12px;
  cursor: pointer;
  transition: box-shadow 0.15s ease-in-out;
  box-shadow: 0px 6px 58px 0px rgba(196, 203, 214, 0.1);

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`

const SearchIcon = styled(PiMagnifyingGlass)`
  color: #6e6e6e;
  flex-shrink: 0;
`

const SearchPlaceholder = styled.span`
  flex: 1;
  text-align: left;
`

const NotificationWrapper = styled.div`
  /* Override NotificationButton size to match Figma spec: 32px container with 24px icon */
  & > div {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  svg {
    width: 24px;
    height: 24px;
    font-size: 24px;
    padding: 0;
  }
`

const AvatarButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: var(--nav-bg-hover);
  color: var(--nav-text-primary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  overflow: hidden;
  transition: opacity 0.15s ease-in-out;

  &:hover {
    opacity: 0.9;
  }
`

interface HeaderProps {
  onSearchClick?: () => void
}

export const Header: React.FC<HeaderProps> = ({ onSearchClick }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const { toggleCollapsed, isSlideOutOpen } = useLeftNavStore()

  // Generate breadcrumbs based on current path
  const breadcrumbItems = useMemo((): BreadcrumbItem[] => {
    const path = location.pathname
    const items: BreadcrumbItem[] = []

    // Targets/Opportunities section
    if (path.startsWith('/opportunities')) {
      const segments = path.split('/').filter(Boolean)
      items.push({
        label: 'Targets',
        hasDropdown: true,
        dropdownItems: [
          {
            label: 'Commercial',
            path: '/opportunities/commercial/restaurants-bars',
            isActive: segments[1] === 'commercial',
          },
          {
            label: 'Non-Commercial',
            path: '/opportunities/non-commercial/education-cu',
            isActive: segments[1] === 'non-commercial',
          },
          {
            label: 'Retail',
            path: '/opportunities/retail/convenience',
            isActive: segments[1] === 'retail',
          },
        ],
      })

      // Add category as secondary breadcrumb
      if (segments.length > 1) {
        const categoryLabel = segments[1]
          .split('-')
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ')
        items.push({ label: categoryLabel, hasDropdown: false })
      }
    }
    // Workspace section (documents, contacts, activity)
    else if (
      path.startsWith('/documents') ||
      path.startsWith('/contacts') ||
      path.startsWith('/activity')
    ) {
      items.push({
        label: 'Workspace',
        hasDropdown: true,
        dropdownItems: [
          {
            label: 'Documents',
            path: '/documents',
            isActive: path.startsWith('/documents'),
          },
          {
            label: 'Contacts',
            path: '/contacts',
            isActive: path.startsWith('/contacts'),
          },
          {
            label: 'Activity',
            path: '/activity',
            isActive: path.startsWith('/activity'),
          },
        ],
      })
    }
    // Settings section
    else if (path.startsWith('/settings')) {
      items.push({
        label: 'Settings',
        hasDropdown: true,
        dropdownItems: [
          {
            label: 'General',
            path: '/settings/general',
            isActive: path.includes('/general'),
          },
          {
            label: 'Team',
            path: '/settings/team',
            isActive: path.includes('/team'),
          },
          {
            label: 'Products',
            path: '/settings/products',
            isActive: path.includes('/products'),
          },
          {
            label: 'Integrations',
            path: '/settings/integrations',
            isActive: path.includes('/integrations'),
          },
        ],
      })
    }
    // Dashboard/Performance
    else if (path.startsWith('/dashboard')) {
      items.push({ label: 'Performance', hasDropdown: false })
    }
    // Campaigns
    else if (path.startsWith('/campaigns')) {
      items.push({ label: 'Campaigns', hasDropdown: false })
    }
    // Deals
    else if (path.startsWith('/deals')) {
      items.push({ label: 'Deals', hasDropdown: false })
    }
    // Rebates
    else if (path.startsWith('/rebates')) {
      items.push({ label: 'Rebates', hasDropdown: false })
    }
    // Samples
    else if (path.startsWith('/samples')) {
      items.push({ label: 'Samples', hasDropdown: false })
    }

    return items
  }, [location.pathname])

  const handleMenuToggle = useCallback(() => {
    toggleCollapsed()
  }, [toggleCollapsed])

  const handleSearchClick = useCallback(() => {
    if (onSearchClick) {
      onSearchClick()
    }
  }, [onSearchClick])

  const handleAvatarClick = useCallback(() => {
    navigate('/settings/profile')
  }, [navigate])

  // Get user initials for avatar fallback
  const userInitials = useMemo(() => {
    if (!user?.first_name && !user?.last_name) return '?'
    const first = user.first_name?.charAt(0) || ''
    const last = user.last_name?.charAt(0) || ''
    return (first + last).toUpperCase()
  }, [user])

  return (
    <HeaderWrapper>
      <MenuToggle
        onClick={handleMenuToggle}
        aria-label="Toggle navigation menu"
        aria-expanded={!useLeftNavStore.getState().isCollapsed}
      >
        <PiList size={24} />
      </MenuToggle>

      <LogoContainer>
        <Logo src={logoWhite} alt="First Bite" />
      </LogoContainer>

      <BreadcrumbsContainer>
        {!isSlideOutOpen && <Breadcrumbs items={breadcrumbItems} />}
      </BreadcrumbsContainer>

      <RightSection>
        <SearchBar onClick={handleSearchClick} aria-label="Search First Bite">
          <SearchIcon size={16} />
          <SearchPlaceholder>Search First Bite</SearchPlaceholder>
        </SearchBar>

        <NotificationWrapper>
          <NotificationButton />
        </NotificationWrapper>

        <AvatarButton onClick={handleAvatarClick} aria-label="User profile">
          {userInitials}
        </AvatarButton>
      </RightSection>
    </HeaderWrapper>
  )
}
